import React from 'react';
import MEASURED from '../../data/measurements.gen.json';
import './landing.css';

/* One small SVG per measured card, drawn from the numbers in
   measurements.gen.json — never from a constant in here. If a number
   changes upstream the picture changes with it; if a glyph cannot be
   computed from the file it is not drawn. Each one is a different plot
   because each number is a different kind of measurement:
     cost     → two bars, per run (routed vs pinned)
     bench    → two bars (routable % saved, cloud vs runner) + the 21–23 % headline tick
     claim    → a 0–35 s timeline with the provisioning stage ticks
     devConsole → a 0–28 s timeline, ask → PR beads, the follow-up as a ghost
     restart  → the remediation sequence: down · down · restarted in 6.3 s */

const W = 200; const H = 56;

export function CostPair() {
  const { routed, pinned, ratio } = MEASURED.compare;
  const max = pinned.usd;
  const bar = (usd) => Math.max(2, (usd / max) * 140);
  return (
    <svg className="mg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Cost per run: routed $${routed.usd.toFixed(3)} vs pinned $${pinned.usd.toFixed(3)}, ${ratio} times cheaper`}>
      <text x="0" y="12" className="mg-k">routed</text>
      <rect x="52" y="4" width={bar(routed.usd)} height="10" rx="2" className="mg-bar is-gold" />
      <text x={52 + bar(routed.usd) + 6} y="12" className="mg-v">${routed.usd.toFixed(3)}</text>
      <text x="0" y="36" className="mg-k">pinned</text>
      <rect x="52" y="28" width={bar(pinned.usd)} height="10" rx="2" className="mg-bar" />
      <text x="52" y="52" className="mg-note">${pinned.usd.toFixed(3)} per run · same two gates approved</text>
    </svg>
  );
}

export function BenchBars() {
  const { cloud, runner, requests } = MEASURED.bench;
  const bar = (pct) => (pct / 100) * 140;
  return (
    <svg className="mg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Routable Dev Console work saved: cloud ${cloud.routablePct}%, sovereign runner ${runner.routablePct}%; headline ${cloud.headlinePct}–${runner.headlinePct}% of the whole bill`}>
      <text x="0" y="12" className="mg-k">cloud</text>
      <rect x="52" y="4" width={bar(cloud.routablePct)} height="10" rx="2" className="mg-bar is-gold" />
      <text x={52 + bar(cloud.routablePct) + 6} y="12" className="mg-v">{cloud.routablePct}%</text>
      <text x="0" y="36" className="mg-k">runner</text>
      <rect x="52" y="28" width={bar(runner.routablePct)} height="10" rx="2" className="mg-bar is-gold" />
      <text x={52 + bar(runner.routablePct) + 6} y="36" className="mg-v">{runner.routablePct}%</text>
      {/* the headline tick: the whole bill, routable + frontier */}
      <line x1={52 + bar(cloud.headlinePct)} y1="1" x2={52 + bar(cloud.headlinePct)} y2="42" className="mg-tick" />
      <line x1={52 + bar(runner.headlinePct)} y1="1" x2={52 + bar(runner.headlinePct)} y2="42" className="mg-tick" />
      <text x="52" y="52" className="mg-note">whole bill {cloud.headlinePct}–{runner.headlinePct}% · {requests} requests · pass {cloud.passBefore} → {cloud.passAfter}</text>
    </svg>
  );
}

function Timeline({ end, ticks, label, ghost = null, done }) {
  const x = (s) => 6 + (s / end) * 186;
  // ticks closer than ~18 px share one label (the claim's repo · host · key
  // land within 0.7 s of each other); label rows alternate per cluster
  const clusters = [];
  ticks.forEach((t) => {
    const last = clusters[clusters.length - 1];
    if (last && x(t.at) - x(last.ticks[last.ticks.length - 1].at) < 18) last.ticks.push(t);
    else clusters.push({ ticks: [t] });
  });
  return (
    <svg className="mg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label}>
      <line x1="6" y1="22" x2="192" y2="22" className="mg-axis" />
      <line x1="6" y1="22" x2={x(done)} y2="22" className="mg-fill" />
      {ghost != null && <line x1={x(done)} y1="22" x2={x(Math.min(end, ghost))} y2="22" className="mg-ghost" />}
      {ticks.map((t, i) => <circle key={t.name} cx={x(t.at)} cy="22" r={i === ticks.length - 1 ? 4 : 2.6} className={`mg-dot ${i === ticks.length - 1 ? 'is-end' : ''}`} />)}
      {(() => {
        // greedy rows: top unless it would overlap a label already on top
        const rows = { 12: [], 38: [] };
        const est = (txt) => txt.length * 6.2;
        return clusters.map((c) => {
          const txt = c.ticks.length > 2 ? `${c.ticks[0].name} … ${c.ticks[c.ticks.length - 1].name}` : c.ticks.map((t) => t.name).join(' · ');
          const cx = x(c.ticks[0].at);
          const u = c.ticks[0].at / end;
          const anchor = u > 0.8 ? 'end' : u < 0.3 ? 'start' : 'middle';
          const w = est(txt);
          const x0 = anchor === 'end' ? cx - w : anchor === 'start' ? cx : cx - w / 2;
          const free = (y) => rows[y].every(([a, b]) => x0 + w < a || x0 > b);
          const y = free(12) ? 12 : free(38) ? 38 : 12;
          rows[y].push([x0, x0 + w]);
          return <text key={c.ticks[0].name} x={cx} y={y} textAnchor={anchor} className="mg-tk">{txt}</text>;
        });
      })()}
      <text x="6" y="52" className="mg-note">0 s</text>
      <text x="192" y="52" textAnchor="end" className="mg-note">{end} s</text>
    </svg>
  );
}

export function ClaimTicks() {
  const c = MEASURED.kit.claim;
  return <Timeline end={Math.ceil(c.seconds)} ticks={c.ticks} done={c.seconds} label={`Claim timeline: ${c.ticks.map((t) => `${t.name} at ${t.at} s`).join(', ')}`} />;
}

export function AskToPr() {
  const d = MEASURED.kit.devConsole;
  return <Timeline end={Math.ceil(d.followUpSeconds)} ticks={d.ticks} done={d.seconds} ghost={d.followUpSeconds} label={`Ask to pull request: ${d.ticks.map((t) => `${t.name} at ${t.at} s`).join(', ')}; follow-up ${d.followUpSeconds} s`} />;
}

export function RestartTimeline() {
  const f = MEASURED.fleet;
  const sec = f.restartMs / 1000;
  const n = f.consecutiveReports;
  // reports every reportEveryMin; the restart lands restartMs after the nth
  const steps = [...Array.from({ length: n }, (_, i) => ({ name: i ? `reported down · ${i + 1}×` : 'reported down', x: 6 + i * 60 })), { name: `restarted · ${sec.toFixed(1)} s`, x: 170 }];
  return (
    <svg className="mg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Service reported down ${n} times, restarted by the fleet in ${sec.toFixed(1)} seconds`}>
      <line x1="6" y1="22" x2="192" y2="22" className="mg-axis" />
      {steps.map((s, i) => (
        <g key={i}>
          <circle cx={s.x} cy="22" r={i === steps.length - 1 ? 4.5 : 3.2} className={i === steps.length - 1 ? 'mg-dot is-ok' : 'mg-dot is-down'} />
          <text x={i === steps.length - 1 ? 192 : s.x} y={i % 2 === 0 ? 12 : 38} textAnchor={i === steps.length - 1 ? 'end' : 'start'} className="mg-tk">{s.name}</text>
        </g>
      ))}
      <text x="6" y="52" className="mg-note">every {f.reportEveryMin} min · {f.service} on {f.slug}</text>
    </svg>
  );
}

const GLYPHS = { cost: CostPair, bench: BenchBars, claim: ClaimTicks, devConsole: AskToPr, restart: RestartTimeline };

export default function MeasureGlyph({ kind }) {
  const G = GLYPHS[kind];
  return G ? <G /> : null;
}
